import { useState, useEffect } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { X, Search } from "lucide-react";
import { api } from "convex/_generated/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { VenueSelector } from "./VenueSelector";
import { DanceStyleSelect } from "./DanceStyleSelect";
import { TagSelect } from "./TagSelect";
import { TimeSelect, followMeridiem } from "./TimeSelect";
import { useLateNightTag } from "./useLateNightTag";
import { RecurrenceSelector, toRecurrenceArgs, fromRecurrenceArgs } from "./RecurrenceSelector";
import type { RecurrenceConfig } from "./RecurrenceSelector";
import { deriveFromDate } from "@/lib/recurrenceConfig";
import { changedSeriesFields, type SeriesFields } from "@/lib/seriesEdit";
import { SeriesScopeDialog } from "./SeriesScopeDialog";
import { LessonSection } from "./LessonSection";
import type { LessonValue } from "./LessonSection";
import { PriceSection, toPriceArgs, priceError, fromPriceArgs } from "./PriceSection";
import type { PriceValue } from "./PriceSection";
import type { Doc, Id } from "convex/_generated/dataModel";

interface EventEditModalProps {
  event: Doc<"events">;
  onClose: () => void;
  onSaved: () => void;
  /** When editing a recurring event: "single" updates only this event, "all" updates every event in the series */
  editScope?: "single" | "all";
}

export function EventEditModal({ event, onClose, onSaved, editScope = "single" }: EventEditModalProps) {
  const updateEvent = useAction(api.events.updateEventWithGeocode);
  const updateSeries = useAction(api.recurring.updateRecurringSeriesWithGeocode);
  const createRecurring = useAction(api.recurring.createRecurringEventWithGeocode);
  const archiveEvent = useMutation(api.moderation.archiveEvent);

  const [title, setTitle] = useState(event.title);
  const [description, setDescription] = useState(event.description ?? "");
  const [date, setDate] = useState(event.date);
  const [startTime, setStartTime] = useState(event.startTime);
  const [endTime, setEndTime] = useState(event.endTime ?? "");
  const [lesson, setLesson] = useState<LessonValue | null>(
    event.lessonStartTime
      ? { firstHour: false, startTime: event.lessonStartTime, endTime: event.lessonEndTime ?? "" }
      : null
  );
  const [venueName, setVenueName] = useState(event.venueName);
  const [venueAddress, setVenueAddress] = useState(event.venueAddress ?? "");
  const [venueId, setVenueId] = useState<Id<"venues"> | null>(event.venueId ?? null);
  const [isManualVenue, setIsManualVenue] = useState(!event.venueId);
  const [venueCoords, setVenueCoords] = useState<{ latitude: number; longitude: number } | null>(
    event.venueLatitude && event.venueLongitude
      ? { latitude: event.venueLatitude, longitude: event.venueLongitude }
      : null
  );
  const [price, setPrice] = useState<PriceValue | null>(
    fromPriceArgs(event.priceMin, event.priceMax, event.priceUnit)
  );
  const [danceStyles, setDanceStyles] = useState<string[]>(event.danceStyles);
  const [mainStyles, setMainStyles] = useState<string[]>(event.mainStyles ?? []);
  const [tags, setTags] = useState<string[]>(event.tags ?? []);
  const [eventUrl, setEventUrl] = useState(event.eventUrl ?? "");

  // An end time past midnight tags the event Late Night without being asked.
  useLateNightTag(startTime, endTime, tags, setTags);

  // Recurring event state (only for non-recurring events)
  const isAlreadyRecurring = !!event.patternId;
  const [recurrenceConfig, setRecurrenceConfig] = useState<RecurrenceConfig | null>(null);

  // Editing the schedule of a series that already exists. The rule lives on
  // the pattern, not the event, so it has to be fetched to be shown.
  const updatePattern = useMutation(api.recurring.updateRecurringPattern);
  const pattern = useQuery(
    api.recurring.getPattern,
    event.patternId ? { patternId: event.patternId } : "skip"
  );
  const [seriesRule, setSeriesRule] = useState<RecurrenceConfig | null>(null);
  const [ruleTouched, setRuleTouched] = useState(false);

  // Under "all events" the date is the series' night: moving it from a Tuesday
  // to a Thursday is how a dance changes nights, so it reschedules the series
  // on its own, without the schedule box being touched as well.
  const seriesMoved = editScope === "all" && isAlreadyRecurring && date !== event.date;
  const rescheduling = ruleTouched || seriesMoved;

  const changeDate = (next: string) => {
    setDate(next);
    // A monthly series on one week of the month follows the date to its week
    // ("2nd Tuesday" moved to the 3rd Thursday is the 3rd Thursday). Several
    // weeks ("2nd & 4th") keep their weeks and take only the new weekday —
    // one date cannot say which of them moved.
    if (
      editScope === "all" &&
      isAlreadyRecurring &&
      next &&
      seriesRule?.period === "monthly" &&
      seriesRule.monthlyType === "nth_weekday" &&
      seriesRule.ordinals.length <= 1
    ) {
      setSeriesRule({ ...seriesRule, ordinals: [deriveFromDate(next).ordinal] });
    }
  };

  // Seed once the pattern arrives, and never again — re-seeding on every
  // render would discard whatever the editor has just picked.
  useEffect(() => {
    if (pattern && !seriesRule) {
      setSeriesRule(fromRecurrenceArgs(pattern, event.date));
    }
  }, [pattern, seriesRule, event.date]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nights of the series that differ from it. A series-wide save asks whether
  // they should change too, so they are fetched up front.
  const specialNights = useQuery(
    api.recurring.getSpecialNights,
    editScope === "all" && event.patternId ? { patternId: event.patternId } : "skip"
  );
  const [askScope, setAskScope] = useState(false);

  /** The form as a series-wide update would send it. */
  const formFields = (): SeriesFields => ({
    title: title.trim(),
    description: description.trim() || undefined,
    startTime,
    endTime: endTime || undefined,
    lessonStartTime: lesson?.startTime || undefined,
    lessonEndTime: lesson?.endTime || undefined,
    venueName: venueName.trim(),
    venueAddress: venueAddress.trim() || undefined,
    venueId: venueId ?? undefined,
    venueLatitude: venueCoords?.latitude,
    venueLongitude: venueCoords?.longitude,
    danceStyles,
    mainStyles,
    tags: tags.length > 0 ? tags : undefined,
    eventUrl: eventUrl.trim() || undefined,
    ...toPriceArgs(price),
  });

  /** The night the modal was opened on, in the same shape. */
  const openedFields = (): SeriesFields => ({
    title: event.title.trim(),
    description: event.description?.trim() || undefined,
    startTime: event.startTime,
    endTime: event.endTime || undefined,
    lessonStartTime: event.lessonStartTime || undefined,
    lessonEndTime: event.lessonEndTime || undefined,
    venueName: event.venueName.trim(),
    venueAddress: event.venueAddress?.trim() || undefined,
    venueId: event.venueId ?? undefined,
    venueLatitude: event.venueLatitude,
    venueLongitude: event.venueLongitude,
    danceStyles: event.danceStyles,
    mainStyles: event.mainStyles ?? [],
    tags: event.tags?.length ? event.tags : undefined,
    eventUrl: event.eventUrl?.trim() || undefined,
    ...toPriceArgs(fromPriceArgs(event.priceMin, event.priceMax, event.priceUnit)),
  });

  const seriesChanges = () => {
    const { fields, labels } = changedSeriesFields(openedFields(), formFields());
    return {
      fields,
      labels: rescheduling ? [seriesMoved ? "Day / date" : "Schedule", ...labels] : labels,
    };
  };

  /**
   * Saves a series-wide edit. Only the fields the editor changed are sent, so
   * each one lands on its own: moving the day leaves every description, tag
   * and price where it was.
   */
  const saveSeries = async (includeSpecial: boolean) => {
    if (!event.patternId) return;
    setAskScope(false);
    setSaving(true);
    setError(null);
    try {
      // Schedule first: it can add and remove occurrences, and the field
      // updates below should land on the set of dates that survives.
      if (rescheduling && seriesRule) {
        const rule = toRecurrenceArgs(seriesRule, date);
        await updatePattern({
          patternId: event.patternId,
          frequency: rule.frequency,
          interval: seriesRule.interval,
          dayOfWeek: rule.dayOfWeek,
          dayOfMonth: rule.dayOfMonth,
          ordinals: rule.ordinals,
          endDate: seriesRule.endDate || undefined,
          anchorDate: date,
          includeSpecial,
        });
      }

      const { fields } = seriesChanges();
      if (Object.keys(fields).length > 0) {
        await updateSeries({
          patternId: event.patternId,
          ...(fields as Partial<SeriesFields> & { venueId?: Id<"venues"> }),
          includeSpecial,
        });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e.message ?? "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) { setError("Title is required."); return; }
    if (!date) { setError("Date is required."); return; }
    if (!startTime) { setError("Start time is required."); return; }
    if (!venueName.trim()) { setError("Venue name is required."); return; }
    if (danceStyles.length === 0) { setError("Select at least one dance style."); return; }
    const priceMessage = priceError(price);
    if (priceMessage) { setError(priceMessage); return; }

    if (editScope === "all" && event.patternId) {
      if (seriesChanges().labels.length === 0) { onClose(); return; }
      const special = (specialNights?.edited.length ?? 0) + (specialNights?.cancelled ?? 0);
      if (special > 0) { setAskScope(true); return; }
      await saveSeries(false);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (recurrenceConfig && !isAlreadyRecurring) {
        // Convert single event to recurring series
        const cfg = recurrenceConfig;
        const { frequency, dayOfWeek, dayOfMonth, ordinals } = toRecurrenceArgs(cfg, date);

        await createRecurring({
          title: title.trim(),
          description: description.trim() || undefined,
          startTime,
          endTime: endTime || undefined,
          lessonStartTime: lesson?.startTime || undefined,
          lessonEndTime: lesson?.endTime || undefined,
          venueName: venueName.trim(),
          venueAddress: venueAddress.trim() || undefined,
          venueId: venueId ?? undefined,
          venueLatitude: venueCoords?.latitude,
          venueLongitude: venueCoords?.longitude,
          danceStyles,
          mainStyles,
          tags: tags.length > 0 ? tags : undefined,
          eventUrl: eventUrl.trim() || undefined,
          ...toPriceArgs(price),
          frequency,
          interval: cfg.interval,
          dayOfWeek,
          dayOfMonth,
          ordinals,
          firstEventOverride: cfg.firstEventDiffers && cfg.firstEventDate ? cfg.firstEventDate : undefined,
          startDate: date,
          endDate: cfg.endDate || undefined,
        });

        // Archive the original single event since the series replaces it
        await archiveEvent({ eventId: event._id });
      } else {
        // Normal single-event update
        await updateEvent({
          eventId: event._id,
          title: title.trim(),
          description: description.trim() || undefined,
          date,
          startTime,
          endTime: endTime || undefined,
          lessonStartTime: lesson?.startTime || undefined,
          lessonEndTime: lesson?.endTime || undefined,
          venueName: venueName.trim(),
          venueAddress: venueAddress.trim() || undefined,
          venueId: venueId ?? undefined,
          venueLatitude: venueCoords?.latitude,
          venueLongitude: venueCoords?.longitude,
          danceStyles,
          mainStyles,
          tags: tags.length > 0 ? tags : undefined,
          eventUrl: eventUrl.trim() || undefined,
          ...toPriceArgs(price),
        });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e.message ?? "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-background rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-background z-10 border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
            <h2 className="font-semibold text-lg text-foreground">Edit Event</h2>
            {editScope === "all" && event.patternId && (
              <p className="text-xs text-amber-500 mt-0.5">Changes will apply to all events in this series</p>
            )}
          </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          {error && (
            <div className="px-6 pb-3">
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Optional details about the event"
            />
          </div>

          {/* Date + Times */}
          {/* Date takes the whole first row; an hour, a minute and AM/PM need
              more width than a third of one. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1.5">Date</label>
              <Input type="date" value={date} onChange={(e) => changeDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Start Time</label>
              <TimeSelect value={startTime} onChange={setStartTime} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">End Time</label>
              <TimeSelect
                value={endTime}
                onChange={setEndTime}
                placeholder="Optional"
                defaultMeridiem={followMeridiem(startTime)}
              />
            </div>
          </div>

          {/* Schedule of an existing series. Only under "all events" — editing
              one night cannot change the rule the whole series runs on. */}
          {isAlreadyRecurring && editScope === "all" && seriesRule && (
            <div className="space-y-2">
              <RecurrenceSelector
                value={seriesRule}
                onChange={(cfg) => {
                  if (!cfg) return;
                  setSeriesRule(cfg);
                  setRuleTouched(true);
                }}
                startDate={date}
                locked
                title="Schedule"
              />
              {rescheduling && (
                <p className="text-xs text-amber-500">
                  Future dates will be regenerated from this schedule.
                  Individually-edited nights and past dates are left alone.
                </p>
              )}
            </div>
          )}

          {/* Lesson */}
          <LessonSection
            eventStartTime={startTime}
            value={lesson}
            onChange={setLesson}
          />

          {/* Price */}
          <PriceSection value={price} onChange={setPrice} />

          {/* Venue */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Venue</label>
            {isManualVenue ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={venueName} onChange={(e) => setVenueName(e.target.value)} placeholder="Venue name" />
                  <AddressAutocomplete
                    value={venueAddress}
                    onChange={(addr) => {
                      setVenueAddress(addr);
                      setVenueCoords(null);
                    }}
                    onPlaceSelect={(place) => {
                      setVenueAddress(place.address);
                      setVenueCoords(place.coords);
                    }}
                    placeholder="Start typing an address..."
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setIsManualVenue(false)}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  <Search className="w-3 h-3" />
                  Search venues instead
                </button>
              </>
            ) : (
              <VenueSelector
                value={venueId}
                venueName={venueName}
                venueAddress={venueAddress}
                onSelect={(venue) => {
                  setVenueId(venue.id);
                  setVenueName(venue.name);
                  setVenueAddress(venue.address);
                  setVenueCoords(
                    venue.latitude !== undefined && venue.longitude !== undefined
                      ? { latitude: venue.latitude, longitude: venue.longitude }
                      : null
                  );
                }}
                onClear={() => {
                  setVenueId(null);
                  setVenueName("");
                  setVenueAddress("");
                  setVenueCoords(null);
                }}
                onManualEntry={(query) => {
                  setIsManualVenue(true);
                  setVenueName(query);
                }}
              />
            )}
          </div>

          {/* Event URL */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Event URL</label>
            <Input
              value={eventUrl}
              onChange={(e) => setEventUrl(e.target.value)}
              placeholder="https://"
              type="url"
            />
          </div>

          {/* Dance Styles */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Dance Styles</label>
            <DanceStyleSelect
              value={danceStyles}
              onChange={setDanceStyles}
              mainStyles={mainStyles}
              onMainStylesChange={setMainStyles}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Tags</label>
            <TagSelect value={tags} onChange={setTags} />
          </div>

          {/* Recurring toggle — only for non-recurring events */}
          {!isAlreadyRecurring && (
            <div className="space-y-2">
              <RecurrenceSelector
                value={recurrenceConfig}
                onChange={setRecurrenceConfig}
                startDate={date}
              />
              {recurrenceConfig && (
                <p className="text-xs text-amber-500">
                  This will create a recurring series and archive the original single event.
                </p>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-3 sticky bottom-0 bg-background">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || (editScope === "all" && !!event.patternId && specialNights === undefined)}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {askScope && specialNights && (
        <SeriesScopeDialog
          changes={seriesChanges().labels}
          edited={specialNights.edited}
          cancelled={specialNights.cancelled}
          onChoose={saveSeries}
          onCancel={() => setAskScope(false)}
        />
      )}
    </div>
  );
}
